import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, MessageSquare, Bell, Send, X, CheckCheck, MessageCircle, Phone, Plus, Clock, Check, BellRing } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const TopBarIcon = ({ icon: Icon, count, color, activeColor, onClick, testId, label }) => (
  <button onClick={onClick} data-testid={testId} className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors group" title={label}>
    <Icon size={20} className={count > 0 ? activeColor : 'text-slate-400 group-hover:text-slate-600'} />
    {count > 0 && (
      <span className={`absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center ${color}`}>
        {count > 999 ? '999+' : count}
      </span>
    )}
  </button>
);

export const TopBar = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ email: 0, chat: 0, bell: 0, wa_mine: 0, wa_other: 0 });
  const prevCountsRef = useRef({ email: 0, chat: 0, bell: 0, wa_mine: 0, wa_other: 0 });
  const [showWaPanel, setShowWaPanel] = useState(false);
  const [showBellPanel, setShowBellPanel] = useState(false);
  const [bellTab, setBellTab] = useState('avvisi');
  const [waUnreadChats, setWaUnreadChats] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [contactsMap, setContactsMap] = useState({});
  const [promOpen, setPromOpen] = useState(false);
  const [promForm, setPromForm] = useState({ titolo: '', descrizione: '', data_ora: '', priorita: 'normale', destinatari: '' });
  const [promNotifiche, setPromNotifiche] = useState([]);
  const [promCount, setPromCount] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const waRef = useRef(null);
  const bellRef = useRef(null);
  const audioCtxRef = useRef(null);
  const firstLoadRef = useRef(true);
  const browserNotifGranted = useRef(false);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(p => { browserNotifGranted.current = p === 'granted'; });
    } else if ('Notification' in window) {
      browserNotifGranted.current = Notification.permission === 'granted';
    }
  }, []);

  const showBrowserNotif = (title, body, tag = 'crm') => {
    if (!browserNotifGranted.current) return;
    try { new Notification(title, { body, icon: '/favicon.ico', tag, silent: true }); } catch {}
  };

  const playNotifSound = (type = 'default') => {
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      if (type === 'wa_mine') {
        osc.frequency.setValueAtTime(660, ctx.currentTime);
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3);
      } else if (type === 'wa_other') {
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        osc.frequency.setValueAtTime(520, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.2);
      } else {
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.08);
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.25);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.25);
      }
    } catch {}
  };

  useEffect(() => {
    fetchCounts();
    loadContacts();
    fetchPromemoria();
    loadAllUsers();
    const iv = setInterval(() => { fetchCounts(); fetchPromemoria(); }, 10000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (waRef.current && !waRef.current.contains(e.target)) setShowWaPanel(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setShowBellPanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadContacts = async () => {
    try {
      const res = await api.get('/whatsapp/contacts');
      const map = {};
      (res.data || []).forEach(c => { map[c.chat_id] = c.nome; });
      setContactsMap(map);
    } catch {}
  };

  const fetchCounts = async () => {
    try {
      const [r, waRes] = await Promise.all([
        api.get('/notifications/unread-counts'),
        api.get('/whatsapp/chats').catch(() => ({ data: { chats: [] } }))
      ]);
      const newCounts = { ...r.data };
      const waChats = waRes.data?.chats || [];
      let waMine = 0, waOther = 0;
      const unread = [];
      waChats.forEach(c => {
        if (c.unreadCount > 0) {
          if (c.assignedTo === user?.id) { waMine += c.unreadCount; }
          else { waOther += c.unreadCount; }
          unread.push(c);
        }
      });
      newCounts.wa_mine = waMine;
      newCounts.wa_other = waOther;
      setWaUnreadChats(unread);
      if (!firstLoadRef.current) {
        const prev = prevCountsRef.current;
        if (newCounts.wa_mine > prev.wa_mine) {
          playNotifSound('wa_mine');
          const c = unread.find(c => c.assignedTo === user?.id);
          if (c) showBrowserNotif('WhatsApp - Chat tua', `${contactsMap[c.id] || c.name || c.id?.split('@')[0]}: ${c.lastMessage?.body || 'Nuovo messaggio'}`, 'wa-mine');
        } else if (newCounts.wa_other > prev.wa_other) {
          playNotifSound('wa_other');
          const c = unread.find(c => c.assignedTo !== user?.id);
          if (c) showBrowserNotif('WhatsApp - Chat libera', `${contactsMap[c.id] || c.name || c.id?.split('@')[0]}: ${c.lastMessage?.body || 'Nuovo messaggio'}`, 'wa-other');
        } else if (newCounts.email > prev.email) {
          playNotifSound('default');
          showBrowserNotif('Nuova Email', 'Hai una nuova email assegnata', 'email');
        } else if (newCounts.chat > prev.chat) {
          playNotifSound('default');
          showBrowserNotif('Chat Interna', 'Nuovo messaggio nella chat', 'chat');
        } else if (newCounts.bell > prev.bell) {
          playNotifSound('default');
          showBrowserNotif('Notifica', 'Hai una nuova notifica', 'bell');
        }
      }
      firstLoadRef.current = false;
      prevCountsRef.current = newCounts;
      setCounts(newCounts);
    } catch {}
  };

  const openBellPanel = async () => {
    setShowWaPanel(false);
    const opening = !showBellPanel;
    setShowBellPanel(opening);
    if (opening) {
      try { const r = await api.get('/notifications'); setNotifications(r.data); } catch {}
    }
  };

  const openWaPanel = () => {
    setShowBellPanel(false);
    setShowWaPanel(prev => !prev);
  };

  const markSingleRead = async (notifId) => {
    try {
      await api.patch(`/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setCounts(prev => ({ ...prev, bell: Math.max(0, prev.bell - 1) }));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/mark-read?tipo=all');
      fetchCounts();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const goToChat = (chatId) => {
    setShowWaPanel(false);
    navigate('/whatsapp', { state: { openChatId: chatId } });
  };

  const sendBroadcast = async () => {
    if (!broadcastTitle.trim()) return;
    setSending(true);
    try {
      await api.post(`/notifications/broadcast?titolo=${encodeURIComponent(broadcastTitle)}&messaggio=${encodeURIComponent(broadcastMsg)}`);
      setBroadcastOpen(false);
      setBroadcastTitle('');
      setBroadcastMsg('');
    } catch {}
    finally { setSending(false); }
  };

  const formatChatName = (chat) => {
    if (contactsMap[chat.id]) return contactsMap[chat.id];
    if (chat.name && !chat.name.includes('@')) return chat.name;
    const num = chat.id?.replace('@c.us', '') || '';
    return num.startsWith('39') ? `+${num.slice(0, 2)} ${num.slice(2, 5)} ${num.slice(5)}` : `+${num}`;
  };

  const loadAllUsers = async () => {
    try { const r = await api.get('/users'); setAllUsers(r.data || []); } catch {}
  };

  const fetchPromemoria = async () => {
    try {
      const r = await api.get('/promemoria/notifiche');
      setPromNotifiche(r.data.notifiche || []);
      setPromCount(r.data.count || 0);
    } catch {}
  };

  const createPromemoria = async () => {
    if (!promForm.titolo.trim()) return;
    setSending(true);
    try {
      await api.post(`/promemoria?titolo=${encodeURIComponent(promForm.titolo)}&descrizione=${encodeURIComponent(promForm.descrizione)}&data_ora=${encodeURIComponent(promForm.data_ora)}&priorita=${promForm.priorita}&destinatari=${encodeURIComponent(promForm.destinatari)}`);
      setPromOpen(false);
      setPromForm({ titolo: '', descrizione: '', data_ora: '', priorita: 'normale', destinatari: 'tutti' });
      fetchPromemoria();
    } catch {}
    finally { setSending(false); }
  };

  const segnaLettoPromemoria = async (id) => {
    try {
      await api.post(`/promemoria/${id}/letto`);
      setPromNotifiche(prev => prev.filter(p => p.id !== id));
      setPromCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const completaPromemoria = async (id) => {
    try {
      await api.post(`/promemoria/${id}/completa`);
      setPromNotifiche(prev => prev.filter(p => p.id !== id));
      setPromCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const getNotifIcon = (tipo) => {
    if (tipo === 'task_completed') return { icon: CheckCheck, color: 'text-emerald-500' };
    if (tipo === 'admin_broadcast') return { icon: Bell, color: 'text-amber-500' };
    if (tipo === 'menzione') return { icon: MessageSquare, color: 'text-blue-500' };
    if (tipo === 'ferie_request') return { icon: Clock, color: 'text-violet-500' };
    return { icon: Bell, color: 'text-slate-400' };
  };

  const getPriorityStyle = (p) => {
    if (p === 'urgente') return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-600' };
    if (p === 'importante') return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-600' };
    return { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-500' };
  };

  const isCollaboratore = user?.ruolo === 'collaboratore';

  const mineChats = waUnreadChats.filter(c => c.assignedTo === user?.id);
  const otherChats = waUnreadChats.filter(c => c.assignedTo !== user?.id);
  const waTotal = counts.wa_mine + counts.wa_other;
  const bellTotal = counts.bell + promCount;

  return (
    <>
      <div className="min-h-[3.5rem] bg-white border-b border-slate-200/80 flex items-center justify-end px-3 sm:px-5 gap-0.5 sm:gap-1 flex-shrink-0 shadow-sm pl-14 md:pl-3" data-testid="top-bar">
        {!isCollaboratore && <TopBarIcon icon={Mail} count={counts.email} color="bg-blue-500" activeColor="text-blue-500" label="Email" testId="notif-email" onClick={() => navigate('/emails')} />}
        {!isCollaboratore && <TopBarIcon icon={MessageSquare} count={counts.chat} color="bg-indigo-500" activeColor="text-indigo-500" label="Chat" testId="notif-chat" onClick={() => navigate('/chat')} />}

        {/* WhatsApp unificato - solo per staff */}
        {!isCollaboratore && (
        <div className="relative" ref={waRef}>
          <TopBarIcon icon={MessageCircle} count={waTotal} color="bg-emerald-500" activeColor="text-emerald-500" label="WhatsApp" testId="notif-wa" onClick={openWaPanel} />
          {showWaPanel && (
            <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] bg-white border border-slate-200 rounded-xl shadow-xl z-50 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0" data-testid="wa-panel">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-emerald-50 rounded-t-xl">
                <div className="flex items-center gap-2">
                  <MessageCircle size={14} className="text-emerald-600" />
                  <h3 className="text-xs font-bold text-emerald-700">WhatsApp</h3>
                </div>
                <button onClick={() => { setShowWaPanel(false); navigate('/whatsapp'); }} className="text-[11px] text-emerald-600 hover:text-emerald-700 font-medium" data-testid="wa-open-all">
                  Apri tutte
                </button>
              </div>
              <ScrollArea className="max-h-[400px]">
                {waTotal === 0 ? (
                  <div className="py-10 text-center">
                    <MessageCircle size={24} className="text-slate-200 mx-auto mb-2" />
                    <p className="text-xs text-slate-400">Nessun messaggio non letto</p>
                  </div>
                ) : (
                  <>
                    {mineChats.length > 0 && (
                      <>
                        <div className="px-4 py-1.5 bg-emerald-50/50 border-b border-emerald-100/50">
                          <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">Le mie chat ({counts.wa_mine})</p>
                        </div>
                        {mineChats.map(c => (
                          <button key={c.id} onClick={() => goToChat(c.id)} className="w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-emerald-50/30 transition-colors" data-testid={`wa-chat-${c.id}`}>
                            <div className="flex items-center gap-2.5">
                              <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                <Phone size={12} className="text-emerald-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs font-semibold text-slate-900 truncate">{formatChatName(c)}</p>
                                  <span className="ml-2 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold text-white bg-emerald-500 flex-shrink-0">{c.unreadCount}</span>
                                </div>
                                <p className="text-[11px] text-slate-500 truncate mt-0.5">{c.lastMessage?.body || 'Nuovo messaggio'}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                    {otherChats.length > 0 && (
                      <>
                        <div className="px-4 py-1.5 bg-orange-50/50 border-b border-orange-100/50">
                          <p className="text-[10px] font-semibold text-orange-600 uppercase tracking-wider">Chat libere ({counts.wa_other})</p>
                        </div>
                        {otherChats.map(c => (
                          <button key={c.id} onClick={() => goToChat(c.id)} className="w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-orange-50/30 transition-colors" data-testid={`wa-chat-${c.id}`}>
                            <div className="flex items-center gap-2.5">
                              <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                <Phone size={12} className="text-orange-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs font-semibold text-slate-900 truncate">{formatChatName(c)}</p>
                                  <span className="ml-2 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold text-white bg-orange-400 flex-shrink-0">{c.unreadCount}</span>
                                </div>
                                <p className="text-[11px] text-slate-500 truncate mt-0.5">{c.lastMessage?.body || 'Nuovo messaggio'}</p>
                                {c.assignedToName && <p className="text-[10px] text-slate-400 mt-0.5">Gestita da {c.assignedToName}</p>}
                              </div>
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </ScrollArea>
            </div>
          )}
        </div>
        )}

        {/* Centro Notifiche unificato (Avvisi + Promemoria) */}
        <div className="relative" ref={bellRef}>
          <TopBarIcon icon={Bell} count={bellTotal} color="bg-amber-500" activeColor="text-amber-500" label="Notifiche" testId="notif-bell" onClick={openBellPanel} />
          {showBellPanel && (
            <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[400px] max-w-[400px] bg-white border border-slate-200 rounded-xl shadow-xl z-50 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0" data-testid="bell-panel">
              {/* Header con tabs */}
              <div className="border-b border-slate-100 rounded-t-xl">
                <div className="flex items-center justify-between px-4 pt-3 pb-0">
                  <h3 className="text-sm font-bold text-slate-900" data-testid="bell-panel-title">Centro Notifiche</h3>
                  <div className="flex gap-1.5">
                    {bellTab === 'avvisi' && notifications.some(n => !n.read) && (
                      <button onClick={markAllRead} className="text-[10px] text-blue-500 hover:text-blue-600 font-medium px-1.5 py-0.5 rounded hover:bg-blue-50 transition-colors" data-testid="mark-all-read">
                        Segna lette
                      </button>
                    )}
                    {bellTab === 'promemoria' && promCount > 0 && (
                      <button onClick={async () => { try { await api.post('/promemoria/segna-tutti-letti'); fetchPromemoria(); } catch {} }} className="text-[10px] text-violet-500 hover:text-violet-600 font-medium px-1.5 py-0.5 rounded hover:bg-violet-50 transition-colors" data-testid="prom-mark-all-read">
                        Segna letti
                      </button>
                    )}
                    {!isCollaboratore && isAdmin && bellTab === 'avvisi' && (
                      <button onClick={() => { setShowBellPanel(false); setBroadcastOpen(true); }} className="text-[10px] text-slate-500 hover:text-slate-700 font-medium flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-slate-100 transition-colors" data-testid="open-broadcast">
                        <Send size={9} /> Broadcast
                      </button>
                    )}
                    {bellTab === 'promemoria' && (
                      <button onClick={() => { setShowBellPanel(false); setPromOpen(true); }} className="text-[10px] text-violet-500 hover:text-violet-600 font-medium flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-violet-50 transition-colors" data-testid="prom-create-btn">
                        <Plus size={9} /> Nuovo
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex px-4 mt-2 gap-0">
                  <button
                    onClick={() => setBellTab('avvisi')}
                    className={`flex-1 text-xs font-medium pb-2 border-b-2 transition-colors ${bellTab === 'avvisi' ? 'text-slate-900 border-slate-900' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
                    data-testid="bell-tab-avvisi"
                  >
                    Avvisi {counts.bell > 0 && <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold text-white bg-amber-500">{counts.bell}</span>}
                  </button>
                  {!isCollaboratore && (
                  <button
                    onClick={() => setBellTab('promemoria')}
                    className={`flex-1 text-xs font-medium pb-2 border-b-2 transition-colors ${bellTab === 'promemoria' ? 'text-violet-700 border-violet-600' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
                    data-testid="bell-tab-promemoria"
                  >
                    Promemoria {promCount > 0 && <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold text-white bg-violet-500">{promCount}</span>}
                  </button>
                  )}
                </div>
              </div>

              {/* Tab Avvisi */}
              {bellTab === 'avvisi' && (
                <ScrollArea className="max-h-[400px]">
                  {notifications.length === 0 ? (
                    <div className="py-10 text-center">
                      <Bell size={24} className="text-slate-200 mx-auto mb-2" />
                      <p className="text-xs text-slate-400">Nessun avviso</p>
                    </div>
                  ) : (
                    notifications.filter(n => !isCollaboratore || n.tipo === 'richiesta_emissione' || n.tipo === 'comunicazione').map(n => {
                      const { icon: NIcon, color } = getNotifIcon(n.tipo);
                      const isUnread = !n.read;
                      return (
                        <div
                          key={n.id}
                          onClick={() => isUnread && markSingleRead(n.id)}
                          className={`px-4 py-3 border-b border-slate-50 transition-all group ${isUnread ? 'bg-blue-50/40 hover:bg-blue-50/60 cursor-pointer' : 'opacity-50 hover:opacity-70'}`}
                          data-testid={`notif-item-${n.id}`}
                        >
                          <div className="flex gap-3 items-start">
                            <div className="relative flex-shrink-0 mt-0.5">
                              <NIcon size={16} className={color} />
                              {isUnread && (
                                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-white" data-testid={`notif-unread-dot-${n.id}`} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs ${isUnread ? 'font-semibold text-slate-900' : 'font-normal text-slate-500'}`}>{n.titolo}</p>
                              <p className={`text-[11px] mt-0.5 line-clamp-2 ${isUnread ? 'text-slate-600' : 'text-slate-400'}`}>{n.messaggio}</p>
                              <p className={`text-[10px] mt-1 ${isUnread ? 'text-slate-400' : 'text-slate-300'}`}>
                                {n.created_at ? format(new Date(n.created_at), 'dd/MM HH:mm', { locale: it }) : ''}
                                {n.sender_nome && <span className="ml-1">- {n.sender_nome}</span>}
                              </p>
                            </div>
                            <button
                              onClick={async (e) => { e.stopPropagation(); try { await api.delete(`/notifications/${n.id}`); setNotifications(prev => prev.filter(x => x.id !== n.id)); } catch {} }}
                              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 transition-all flex-shrink-0"
                              data-testid={`notif-delete-${n.id}`}
                            >
                              <X size={12} className="text-slate-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </ScrollArea>
              )}

              {/* Tab Promemoria */}
              {bellTab === 'promemoria' && (
                <ScrollArea className="max-h-[400px]">
                  {promNotifiche.length === 0 ? (
                    <div className="py-10 text-center">
                      <BellRing size={24} className="text-slate-200 mx-auto mb-2" />
                      <p className="text-xs text-slate-400">Nessun promemoria attivo</p>
                      <Button size="sm" variant="outline" className="mt-3 text-xs" onClick={() => { setShowBellPanel(false); setPromOpen(true); }}>
                        <Plus size={12} className="mr-1" /> Crea Promemoria
                      </Button>
                    </div>
                  ) : (
                    promNotifiche.map(p => {
                      const ps = getPriorityStyle(p.priorita);
                      return (
                        <div key={p.id} className={`px-4 py-3 border-b border-slate-50 ${ps.bg}`} data-testid={`prom-item-${p.id}`}>
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <p className={`text-xs font-semibold ${ps.text}`}>{p.titolo}</p>
                                {p.priorita !== 'normale' && (
                                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${ps.badge}`}>
                                    {p.priorita === 'urgente' ? 'Urgente' : 'Importante'}
                                  </span>
                                )}
                              </div>
                              {p.descrizione && <p className="text-[11px] text-slate-500 mb-1">{p.descrizione}</p>}
                              <div className="flex items-center gap-2 text-[10px] text-slate-400 flex-wrap">
                                <span>Da: {p.creato_da_nome || 'Sistema'}</span>
                                {p.destinatario_nome && <span className="text-violet-500 font-medium">A: {p.destinatario_nome}</span>}
                                {!p.destinatario_nome && p.destinatari && p.destinatari.length > 0 && p.destinatari[0] !== 'tutti' && (
                                  <span className="text-violet-500 font-medium">A: {allUsers.find(u => u.id === p.destinatari[0])?.nome || 'utente'}</span>
                                )}
                                {p.data_ora && <span className="flex items-center gap-0.5"><Clock size={9} /> {format(new Date(p.data_ora), 'dd/MM HH:mm', { locale: it })}</span>}
                              </div>
                            </div>
                            <div className="flex gap-0.5 flex-shrink-0">
                              <button onClick={() => completaPromemoria(p.id)} className="p-1.5 rounded-md hover:bg-emerald-100 text-slate-300 hover:text-emerald-600 transition-colors" title="Completato" data-testid={`prom-complete-${p.id}`}>
                                <Check size={14} />
                              </button>
                              <button onClick={() => segnaLettoPromemoria(p.id)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-300 hover:text-slate-500 transition-colors" title="Segna letto" data-testid={`prom-read-${p.id}`}>
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </ScrollArea>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invia Notifica a Tutti</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <Input placeholder="Titolo" value={broadcastTitle} onChange={(e) => setBroadcastTitle(e.target.value)} data-testid="broadcast-title" />
            <Textarea placeholder="Messaggio..." value={broadcastMsg} onChange={(e) => setBroadcastMsg(e.target.value)} rows={4} data-testid="broadcast-msg" />
            <Button onClick={sendBroadcast} disabled={sending || !broadcastTitle.trim()} className="w-full" data-testid="broadcast-send">
              <Bell size={14} className="mr-2" /> Invia a Tutti
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Promemoria Dialog */}
      <Dialog open={promOpen} onOpenChange={setPromOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuovo Promemoria</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs font-medium text-slate-600">Titolo *</Label>
              <Input placeholder="Es: Richiamare cliente Rossi" value={promForm.titolo} onChange={(e) => setPromForm(f => ({ ...f, titolo: e.target.value }))} className="mt-1" data-testid="prom-form-titolo" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Descrizione</Label>
              <Textarea placeholder="Dettagli aggiuntivi..." value={promForm.descrizione} onChange={(e) => setPromForm(f => ({ ...f, descrizione: e.target.value }))} rows={2} className="mt-1" data-testid="prom-form-descrizione" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Data e Ora</Label>
                <Input type="datetime-local" value={promForm.data_ora} onChange={(e) => setPromForm(f => ({ ...f, data_ora: e.target.value }))} className="mt-1" data-testid="prom-form-data" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Priorita'</Label>
                <Select value={promForm.priorita} onValueChange={(v) => setPromForm(f => ({ ...f, priorita: v }))}>
                  <SelectTrigger className="mt-1" data-testid="prom-form-priorita"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="importante">Importante</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Assegna a *</Label>
              <Select value={promForm.destinatari} onValueChange={(v) => setPromForm(f => ({ ...f, destinatari: v }))}>
                <SelectTrigger className="mt-1" data-testid="prom-form-destinatari"><SelectValue placeholder="Seleziona utente..." /></SelectTrigger>
                <SelectContent>
                  {allUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={createPromemoria} disabled={sending || !promForm.titolo.trim() || !promForm.destinatari} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="prom-form-submit">
              <BellRing size={14} className="mr-2" /> Crea Promemoria
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
